import { useForm } from 'react-hook-form';
import { FormProvider, SelectTag, Table, TextField } from '../../../../../components';
import {
  Container,
  Dialog,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
  TextField as MuiTextField,
  Box,
  Button
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { dropdownFetch, getCurrentDateTime, getKeyPathValue, getMonthYear, request, toast } from '../../../../../utils';
import CloseIcon from '@mui/icons-material/Close';
import { useLocation } from 'react-router-dom';
import { CustomButtonn } from '../../../../../components';
import { StockAdjustmentAddDetails } from './StockAdjustmentAddDetails';


const TABLE_HEADER = [
  {
    header: ' Date',
    accessor: (row) => `${getCurrentDateTime('date', row.date)}`
  },
  {
    header: 'Adjustment No.',
    accessor: 'adjustment_no'
  },
  {
    header: 'Location',
    accessor: 'location.warehouse_name'
  },
  {
    header: 'Status',
    accessor: 'status',
    sx: { textTransform: 'uppercase' }
  }
];

const STOCK_ADJUSTMENT_TABLE = [
  {
    header: 'Location Name',
    accessor: (row)=> row?.location_name || row?.location?.warehouse_name
  },
  {
    header: 'Product Name',
    accessor: (row)=> row?.product_name || row?.product?.main?.name
  },
  {
    header: 'Batch No.',
    accessor: (row)=> row?.batch_no
  },
  {
    header: 'Exp. Date',
    accessor: (row)=>  getMonthYear(row?.exp_date)
  },
  {
    header: 'Cur. Stock',
    accessor: (row)=> row?.current_stock
  },
  {
    header: 'Unit Name',
    accessor: (row)=> row?.unit_name
  },
  {
    header: 'Adjustment Stock',
    accessor: (row)=> row?.adj_qty
  },
  {
    header: 'New Qty',
    accessor: (row)=> row?.new_qty
  },
  {
    header: 'Reason',
    accessor: (row)=> row?.reason
  },
  {
    header: 'Rate',
    accessor: (row)=> row?.rate
  },
];

const initialStockAdjustmentDataObj = {
  data: [],
  loading: false
};

export const PharmacyStockAdjustment = () => {
  const { pathname } = useLocation();
  const lastPathSegment = pathname.split('/').pop()
  const pathParts = pathname.split('/');
  const organisation = pathParts[1];
  const [detailList, setDetailList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentAction, setCurrentAction] = useState();
  const [selectedRecord, setSelectedRecord] = useState();
  const [stockAdjustmentDataObj, setStockAdjustmentDataObj] = useState(initialStockAdjustmentDataObj);
  const [openQueryDialog, setOpenQueryDialog] = useState(false);
  const [input, setInput] = useState('');
  const [addDetailsDialogOpen, setAddDetailsDialogOpen] = useState(false);
  const [locationDropdown, setLocationDropdown] = useState([]);
  const user = JSON.parse(localStorage.getItem('user'));

  const methods = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        location: Yup.string().required(' ')
      })
    ),
    defaultValues: {
      date: new Date().toISOString().split('T')[0],
      time: getCurrentDateTime('time')
    },
    mode: 'all'
  });


  const { handleSubmit, watch, reset, setValue } = methods;

  const getLocationDropdown = useCallback(async () => {
    const data = await dropdownFetch('warehouse');
    setLocationDropdown(data);
  }, []);

  useEffect(() => {
    getLocationDropdown();
  }, [getLocationDropdown]);

  useEffect(() => {
    if (selectedRecord) {
      const keyValuePair = getKeyPathValue(selectedRecord, 'bill', [
        '__v',
        '_id',
        'createdAt',
        'status',
        'service_items',
        'uhid',
        'complaints',
        'diagnos',
        'medicine',
        'vital'
      ]);
      keyValuePair.forEach(({ key, value }) => setValue(key, value));
          const formattedRegDate = 
            selectedRecord?.date
          ? new Date(selectedRecord.date).toISOString().split('T')[0]
          : '';
      setValue('date', formattedRegDate);
      setDetailList(selectedRecord?.add_details)
      setValue('location',selectedRecord.location._id)
      setValue('remarks',selectedRecord.remarks)
      setValue('adjustment_no',selectedRecord?.adjustment_no);
    } else {
      reset();
    }
  }, [selectedRecord]);

  const formSubmit = async (formValues) => {
    if(detailList.length === 0){
      toast("Please Add the Details First",{variant:"warning"})
      return 
    }
    setLoading(true);
    let editFlag = false;
    if (selectedRecord && currentAction === 'edit') {
      editFlag = true;
    }
    const response = await request(editFlag ? '/stock-adjustment-update' : '/stock-adjustment-create', {
      method: editFlag ? 'PUT' : 'POST',
      params: editFlag ? selectedRecord._id : null,
      body:{...formValues,add_details:detailList}
    });
    if (response?.success) {
      toast(`Successfully ${editFlag ? 'updated' : 'created'} Stock Adjustment`,{ variant: 'success' });
      setSelectedRecord(response.data);
      setCurrentAction();
      reset();
    } else {
      toast(`Failed to ${editFlag ? 'update' : 'create'} Stock Adjustment`,{
        variant: 'error'
      });
    }
    setLoading(false);
  };
  
  const handleRowDelete = (rowId) => {
    setDetailList((prev) =>
      prev.filter((data) => {
        return data.product !== rowId;
      })
    );
  };

  const handleDeleteRecord = async () => {
    setLoading(true);
    const response = await request('/stock-adjustment-delete', {
      method: 'DELETE',
      params: selectedRecord._id
    });
    if (response?.success) {
      toast('Successfully deleted Stock Adjustment', { variant: 'success' });
      setSelectedRecord();
    } else {
      toast('Failed to delete Stock Adjustment', { variant: 'error' });
    }
    setLoading(false);
  };


  const handleCancelRecord = async () => {
    setLoading(true);
    const response = await request('/stock-adjustment-cancel', {
      method: 'DELETE',
      params: selectedRecord._id
    });
    if (response?.success) {
      toast('Successfully cancel Stock Adjustment', { variant: 'success' });
      setSelectedRecord();
    } else {
      toast('Failed to cancel Stock Adjustment', { variant: 'error' });
    }
    setLoading(false);
  };



const handleAuthorise = async (formValues) => {
  if (detailList.length === 0) {
    toast("First Add the Details First", { variant: "warning" });
    return;
  }
  setLoading(true);
  const updatedProductDetails = detailList.map((productDetail) => {
    const adjQty = Number(productDetail.adj_qty || 0);
    const inQty = adjQty > 0 ? adjQty : 0;
    const outQty = adjQty < 0 ? Math.abs(adjQty) : 0;
    return {
      ...productDetail,
      product: productDetail.product?._id,
      type: "Stock Adjustment",
      hsn_code: productDetail.hsn_code,
      mrp: productDetail.mrp,
      conversion: productDetail.conversion,
      supplier: productDetail.supplier,
      unit: productDetail.unit,
      bill_qty: productDetail.bill_qty,
      packing: productDetail.packing,
      free_qty: productDetail.free_qty,
      rate: productDetail.rate,
      gst: productDetail.gst,
      dis_amt: productDetail.dis_amt,
      dis_percent: productDetail.dis_percent,
      batch_no: productDetail.batch_no,
      total_qty: productDetail.total_qty,
      mfg_date: productDetail.mfg_date,
      exp_date: productDetail.exp_date,
      inQty: inQty,
      outQty: outQty,
      purRate: Number(productDetail.rate || 0),
      saleRate: Number(productDetail.sale_rate || 0),
      purAmount: Number(productDetail.amount || 0),
      saleAmount: Number(productDetail.amount || 0),
    };
  });
  const payload = {
    doc_no: selectedRecord.adjustment_no,
    productDetails: updatedProductDetails,
    createdBy: user.name,
    organisation
  };
  try {
    const response1 = await request("/stock-master-create", {
      method: 'POST',
      body: payload
    });
    if (!response1?.success) {
      throw new Error("Failed to submit to StockMaster.");
    }
    const response2 = await request(`/stock-adjustment-authorise`, {
      method: 'PUT',
      params: selectedRecord?._id
    });
    if (!response2?.success) {
      throw new Error("Failed to update stock status.");
    }
    toast('Stock authorised and status updated successfully!', { variant: 'success' });
  } catch (error) {
    toast(`Error: ${error.message}`, { variant: 'error' });
  } finally {
    setLoading(false);
  }
};


  const handleQueryWindow = async () => {
    setOpenQueryDialog(true);
    setStockAdjustmentDataObj({ loading: true });
    const response = await request('/stock-adjustment-list', { method: 'GET' });
    if (response.success) {
      const sortedData = response.data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      const filteredData = sortedData.filter(
        (item) => item.status === 'active' || item.status === 'inactive' || item.status === 'default'
      );
      setStockAdjustmentDataObj({ loading: false, data: filteredData });
    } else {
      toast('Error in fetching data', { variant: 'error' });
      setStockAdjustmentDataObj({ loading: false });
    }
  };

    const onDetailAddHandler = (data) => {
    const existingDetail = detailList.find(({ product }) => product === data.product);
    if (existingDetail) {
      toast('Product already chosen', { variant: 'error' });
      return;
    } else {
      toast('Product Added Successfully', { variant: 'success' });
    }
    setDetailList((prev) => [
      ...prev,
      {
        ...data,
        type: 'Stock Adjustment',
      }
    ]);
  };


    useEffect(() => {
    handleStockAdjustmentSearch(input);
  }, [input]);

  const handleStockAdjustmentSearch = async () => {
    const lowercasedInput = input.toUpperCase().trim();
    const limit = 10;
    try {
      const response = await request('/stock-adjustment-list', {
        method: 'GET',
        query: { name: lowercasedInput, limit: limit }
      });
      if (response.success) {
        const sortedData = response.data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        const filteredData = sortedData.filter(
          (item) => item.status === 'active' || item.status === 'inactive' || item.status === 'default'
        );
        setStockAdjustmentDataObj({ loading: false, data: filteredData });
      } else {
        toast('Error in fetching dataaaaa', { variant: 'error' });
        setStockAdjustmentDataObj({ loading: false, data: [] });
      }
    } catch (error) {
      console.error('Error during search:', error);
      toast('Error during search', { variant: 'error' });
      setStockAdjustmentDataObj({ loading: false, data: [] });
    }
  };

  const handleChange = (e) => {
    const newInput = e.target.value;
    setInput(newInput);
  };


  return (
    <>
      <Paper sx={{ p: 0, flex: 1, borderRadius: 0, boxShadow: 'none' }}>
        <Container
          sx={{
            mt: 2,
            p: 0.2,
            border: '1px solid',
            borderColor: 'grey.300',
            borderRadius: 1,
            width: '100%',
            maxWidth: 'none',
            minWidth: '100%',
            boxSizing: 'border-box'
          }}
        >
          <FormProvider methods={methods} onSubmit={handleSubmit(formSubmit)}>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="space-between"
              alignItems="center"
              sx={{ pb: 0.5, mb: 3, borderBottom: '1px solid', borderColor: 'grey.300' }}
            >
              <Typography color="primary" variant="h3" sx={{ fontWeight: 700 }}>
                Stock Adjustment
              </Typography>
              <Stack direction="row" justifyContent="flex-end" sx={{ mb: 3 }} spacing={1}>
                <CustomButtonn
                  id={`${lastPathSegment}Write`}
                  variant="contained"
                  disabled={['edit', 'new'].includes(currentAction)}
                  onClick={() => {
                    setSelectedRecord();
                    setCurrentAction('new');
                    setDetailList([]);
                    reset();
                  }}
                >
                  New
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Update`}
                  variant="contained"
                  disabled={['new', 'edit'].includes(currentAction) || !selectedRecord}
                  onClick={() => setCurrentAction('edit')}
                >
                  Edit
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Print`}
                  variant="contained"
                  onClick={() => window.open(`/print${pathname}?refId=${selectedRecord._id}`, '_blank')}
                  disabled={!selectedRecord || currentAction}
                >
                  Print
                </CustomButtonn>
                <CustomButtonn
                  id={'Submit'}
                  type="submit"
                  variant="contained"
                  disabled={!['edit', 'new'].includes(currentAction) || loading}
                >
                  Save
                </CustomButtonn>
                <CustomButtonn
                  id={'Cancel'}
                  variant="contained"
                  disabled={['edit', 'new'].includes(currentAction) || loading}
                  onClick={() => {
                    setCurrentAction();
                    handleCancelRecord();
                  }}
                >
                  Cancel
                </CustomButtonn>
                <CustomButtonn
                  id={`isSuperAdmin`}
                  variant="contained"
                  disabled={!selectedRecord || ['new', 'edit'].includes(currentAction) || loading}
                  onClick={handleDeleteRecord}
                >
                  Delete
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Read`}
                  variant="contained"
                  onClick={handleQueryWindow}
                  disabled={['new', 'edit'].includes(currentAction)}
                >
                  Query
                </CustomButtonn>
                  <CustomButtonn
                  id={'Submit'}
                  variant="contained"
                  disabled={['new', 'edit'].includes(currentAction) || !selectedRecord}
                  onClick={handleAuthorise}
                >
                  Authorise
                </CustomButtonn>
                <CustomButtonn
                  id={'Exit'}
                  variant="contained"
                  onClick={() => {
                    setCurrentAction('');
                  }}
                >
                  Exit
                </CustomButtonn>
              </Stack>
            </Stack>

            <Grid container spacing={2}>
              <Grid item xs={6} sx={{ display: 'flex', gap: 0.7, flexDirection: 'column' }}>
                <Stack spacing={0.5} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1rem', fontSize: '1.4rem' }}>
                    Adjustment No.
                  </Typography>
                  <TextField
                    inputProps={{ style: { fontSize: '1.3rem', padding: '5px' } }}
                    disabled
                    sx={{ flex: 0.8 }}
                    name="adjustment_no"
                    placeholder="Adjustment no."
                  />
                </Stack>
                <Stack spacing={0.5} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1rem', fontSize: '1.4rem' }}>
                    Remarks
                  </Typography>
                  <TextField
                    inputProps={{ style: { fontSize: '1.3rem', padding: '5px' } }}
                    disabled={!['new', 'edit'].includes(currentAction)}
                    sx={{ flex: 0.8 }}
                    name="remarks"
                    placeholder="Remarks"
                  />
                </Stack>
              </Grid>
              <Grid item xs={6} sx={{ display: 'flex', gap: 0.7, flexDirection: 'column' }}>
                <Stack spacing={0.5} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Date</Typography>
                  <TextField
                    inputProps={{ style: { fontSize: '1.3rem', padding: '5px' } }}
                    disabled={!['new', 'edit'].includes(currentAction)}
                    sx={{ flex: 0.8 }}
                    name="date"
                    type="date"
                  />
                </Stack>
                <Stack spacing={0.5} direction="row" alignItems="center">
                  <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.3rem', fontSize: '1.4rem' }}>
                    Location
                  </Typography>
                  <SelectTag
                    menus={locationDropdown.map((data) => ({ ...data, name: data.warehouse_name }))}
                    disabled={!['new', 'edit'].includes(currentAction)}
                    formControlSX={{ flex: 0.8 }}
                    name="location"
                    placeholder="Select location"
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} sx={{ minHeight: '45rem' }}>
                <Stack spacing={0.3} sx={{ flexDirection: 'column', height: '100%' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'end' }}>
                    <Button
                      disabled={!['new', 'edit'].includes(currentAction) || !watch('location')}
                      id={`${lastPathSegment}Read`}
                      variant="contained"
                      onClick={()=> setAddDetailsDialogOpen(true)}
                    >
                      Add Details
                    </Button>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Table
                      containerSX={{ p: 1, flex: 1, border: '1px solid rgba(0,0,0,0.27)', minHeight: '30rem' }}
                      data={detailList.map((data)=> ({...data,_id:data.product})) || []}
                      columns={STOCK_ADJUSTMENT_TABLE}
                      size="small"
                      showDeleteAction={true}
                      onDeleteHandler={handleRowDelete}
                    />
                  </Box>
                </Stack>
              </Grid>
            </Grid>
          </FormProvider>
        </Container>
      </Paper>

      <Dialog
        open={openQueryDialog}
        onClose={() => setOpenQueryDialog(false)}
        PaperProps={{ sx: { width: '80%', p: 2 } }}
        maxWidth="xl"
        scroll="body"
      >
        <Stack
          padding={0}
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          borderBottom="1px solid"
          borderColor="grey.300"
        >
          <Typography variant="h4" fontWeight="bold">
            Stock Adjustment Query Window
          </Typography>
          <IconButton onClick={() => setOpenQueryDialog(false)}>
            <CloseIcon color="error" fontSize="large" />
          </IconButton>
        </Stack>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ mt: 0 }}>
          <MuiTextField
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-root': {
                padding: '0',
                fontSize: '1.4rem'
              },
              '& .MuiOutlinedInput-input': {
                padding: '8px 1.4rem'
              },
              '& .MuiInputLabel-root': {
                fontSize: '1.2rem'
              }
            }}
            label="Search By Adjustment No."
            variant="outlined"
            onChange={handleChange}
            value={input}
          />
        </Stack>
        <Table
          sx={{ mt: 0.5 }}
          columns={TABLE_HEADER}
          data={stockAdjustmentDataObj.data || []}
          loading={stockAdjustmentDataObj.loading}
          onRowClick={(row) => { 
            if(row?.authorise === "true"){
              toast("You can not Edit this Document.It has been already authorise",{variant:"warning"})
              return 
            }
            setSelectedRecord(row);
            setOpenQueryDialog(false);
          }}
        />
      </Dialog>

      <Dialog
        open={addDetailsDialogOpen}
        onClose={() => setAddDetailsDialogOpen(false)}
        PaperProps={{ sx: { width: '80%', height: '90vh', p: 2 } }}
        maxWidth="xl"
        scroll="body"
      >
        <StockAdjustmentAddDetails
          onClose={() => setAddDetailsDialogOpen(false)}
          onAdd={onDetailAddHandler}
        />
      </Dialog>
    </>
  );
};
